/* eslint-disable */
import React from "react";
import { Box, Grid, Step, StepContent, StepLabel, Stepper, Theme } from "@mui/material";
import { StriimTypography } from "@striim/striim-ui-v2";
// @ts-ignore
import SuccessIcon from "src/modules/wizards/common/stepper/Images/success.svg";

interface VerticalStepperStep {
    label: string;
    content: React.ReactNode;
    transient?: boolean;
    noIndent?: boolean;
}

interface VerticalStepperProps {
    activeStep: number;
    steps: VerticalStepperStep[];
}

const styles = {
    stepper: {
        width: "100%",
        "& .MuiStep-root": { padding: 0 },
        "& .MuiStepConnector-root": { ml: 1.25 },
        "& .Mui-completed.MuiStepConnector-root .MuiStepConnector-line": {
            borderColor: (theme: Theme) => theme.palette.primary[700]
        },
        "& .Mui-disabled.MuiStepConnector-root .MuiStepConnector-line": {
            borderColor: (theme: Theme) => theme.palette.greyscale[400]
        },
        "& .Mui-active.MuiStepConnector-root .MuiStepConnector-line": {
            borderColor: (theme: Theme) => theme.palette.greyscale[400]
        },
        "& .MuiStepLabel-label.Mui-active .MuiTypography-root": {
            color: (theme: Theme) => theme.palette.primary[700]
        },
        "& .MuiStepLabel-label.Mui-disabled .MuiTypography-root": {
            color: (theme: Theme) => theme.palette.greyscale[400]
        },
        "& .MuiStepContent-root": { ml: 1.25 }
    }
};

const iconContainerBase = {
    height: (theme: Theme) => theme.spacing(2.5),
    width: (theme: Theme) => theme.spacing(2.5),
    borderRadius: "50%"
};

const iconStyles = {
    success: {
        ...iconContainerBase,
        "& svg": { width: "100%", height: "100%", fill: "transparent" }
    },
    active: {
        ...iconContainerBase,
        backgroundColor: (theme: Theme) => theme.palette.secondary.main,
        "& .MuiTypography-root": {
            color: (theme: Theme) => theme.palette.greyscale[50]
        }
    },
    inactive: {
        ...iconContainerBase,
        border: (theme: Theme) => `1px solid ${theme.palette.greyscale[400]}`,
        "& .MuiTypography-root": {
            color: (theme: Theme) => theme.palette.greyscale[400],
            marginTop: "-1px"
        }
    }
};

interface StepIconProps {
    active: boolean;
    completed: boolean;
    icon: number;
}

const StepIcon = ({ active, completed, icon }: StepIconProps) => {
    if (completed) {
        return (
            <Box sx={iconStyles.success}>
                <SuccessIcon />
            </Box>
        );
    }

    return (
        <Grid container justifyContent="center" sx={active ? iconStyles.active : iconStyles.inactive}>
            <StriimTypography variant="caption">{icon}</StriimTypography>
        </Grid>
    );
};

const VerticalStepper = ({ activeStep, steps }: VerticalStepperProps) => {
    const visibleSteps = React.useMemo(() => steps.filter(step => !step.transient), [steps]);

    const visibleActiveIndex = React.useMemo(() => {
        if (!steps[activeStep]?.transient) {
            return visibleSteps.indexOf(steps[activeStep]);
        }
        let last = visibleSteps[0];
        for (let i = 0; i < activeStep; i++) {
            if (!steps[i].transient) last = steps[i];
        }
        return visibleSteps.indexOf(last);
    }, [activeStep, steps, visibleSteps]);

    return (
        <Stepper activeStep={visibleActiveIndex} orientation="vertical" sx={styles.stepper}>
            {visibleSteps.map((step, visIdx) => {
                const originalIndex = steps.indexOf(step);

                const isActive = visIdx === visibleActiveIndex;
                const content = isActive ? steps[activeStep]?.content : null;

                return (
                    <Step key={step.label} sx={step.noIndent ? { "& .MuiStepContent-root": { ml: 0, pl: 0 } } : {}}>
                        <StepLabel StepIconComponent={StepIcon}>
                            <StriimTypography variant="h3">{step.label}</StriimTypography>
                        </StepLabel>
                        <StepContent>{content}</StepContent>
                    </Step>
                );
            })}
        </Stepper>
    );
};

export default VerticalStepper;
